/* article-list.js */
/* eslint-disable no-console */
import {
  loadRuntimeApiConfig,
  logStagingZetaApiUsage
} from "../config/runtime-api-config";

(() => {
  "use strict";

  const DEFAULTS = {
    endpoint: "",
    fields: "id,title,url,thumbnail,date,tags",
    initialCount: 10,
    moreCount: 10,
    fetchSize: 30,
    maxFetchPages: 10
  };

  const SELECTOR = ".js-article-list";

  function toInt(value, fallback) {
    const n = Number(value);
    return Number.isFinite(n) && n >= 0 ? n : fallback;
  }

  function splitTags(raw) {
    if (!raw) return [];
    return String(raw)
      .split(/[,\s]+/)
      .map((tag) => tag.replace(/^#/, "").trim())
      .filter(Boolean);
  }

  function normalizeUrl(url) {
    if (!url) return "";
    try {
      return new URL(url, location.origin).pathname.replace(/\/+$/, "");
    } catch (_) {
      return String(url).replace(/\/+$/, "");
    }
  }

  function parseDate(raw) {
    if (!raw) return null;
    const text = String(raw).trim();

    if (/^\d{4}-\d{2}-\d{2}/.test(text)) return new Date(text);
    if (/^\d{4}\.\d{2}\.\d{2}$/.test(text)) {
      const [y, m, d] = text.split(".");
      return new Date(`${y}-${m}-${d}`);
    }
    if (/^\d{8}$/.test(text)) {
      const y = text.slice(0, 4);
      const m = text.slice(4, 6);
      const d = text.slice(6, 8);
      return new Date(`${y}-${m}-${d}`);
    }

    const dt = new Date(text);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  function formatDate(raw) {
    const dt = parseDate(raw);
    if (!dt) return raw || "";
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const d = String(dt.getDate()).padStart(2, "0");
    return `${y}.${m}.${d}`;
  }

  function isNewWithinDays(raw, days = 14) {
    const dt = parseDate(raw);
    if (!dt) return false;
    const diff = Date.now() - dt.getTime();
    return diff >= 0 && diff <= days * 24 * 60 * 60 * 1000;
  }

  function normalizeResponse(json) {
    const result = json && json.result ? json.result : json || {};
    const itemsRaw = Array.isArray(result.items)
      ? result.items
      : Array.isArray(result.list)
      ? result.list
      : [];
    // API返却フィールド移行中でも既存テンプレートを壊さないよう互換マッピングする
    const items = itemsRaw.map((item) => {
      if (!item) return item;

      const mapped = { ...item };

      // tags -> hashtag
      if (mapped.tags != null && mapped.hashtag == null) {
        mapped.hashtag = mapped.tags;
      }

      // imagepath -> thumbnail
      if (mapped.imagepath != null && mapped.thumbnail == null) {
        mapped.thumbnail = mapped.imagepath;
      }

      return mapped;
    });
    const totalRaw =
      result?.attribute?.total ??
      result?.total ??
      json?.total ??
      items.length;
    const total = Number(totalRaw);
    return {
      items,
      total: Number.isFinite(total) ? total : items.length
    };
  }

  function readOrTagsFromDom(sourceSelector) {
    if (!sourceSelector) return [];
    const source = document.querySelector(sourceSelector);
    if (!source) return [];

    const tags = [];

    const datasetTags = splitTags(source.dataset.tags);
    tags.push(...datasetTags);

    source.querySelectorAll("[data-tag-value]").forEach((el) => {
      const v = el.getAttribute("data-tag-value");
      if (v) tags.push(...splitTags(v));
    });

    source.querySelectorAll(".m-tag").forEach((el) => {
      const t = (el.textContent || "").replace(/^#/, "").trim();
      if (t) tags.push(t);
    });

    return Array.from(new Set(tags));
  }

  class ArticleList {
    constructor(root, runtimeConfig = {}) {
      this.root = root;
      this.mode = root.dataset.mode || "search";
      this.endpoint =
        root.dataset.endpoint ||
        runtimeConfig.searchEndpoint ||
        DEFAULTS.endpoint;
      this.fields = root.dataset.fields || DEFAULTS.fields;

      this.initialCount = toInt(root.dataset.initialCount, DEFAULTS.initialCount);
      this.moreCount = toInt(root.dataset.moreCount, DEFAULTS.moreCount);
      this.fetchSize = Math.max(1, toInt(root.dataset.fetchSize, DEFAULTS.fetchSize));
      this.maxFetchPages = Math.max(1, toInt(root.dataset.maxFetchPages, DEFAULTS.maxFetchPages));

      this.template = this.resolveTemplate();
      this.button = this.resolveMoreButton();

      this.currentPage = 0;
      this.total = Infinity;
      this.renderedCount = 0;
      this.matchPool = [];
      this.seenUrls = new Set();
      this.exhausted = false;
      this.loading = false;

      this.currentPath = normalizeUrl(
        this.root.dataset.currentUrl || location.pathname
      );
      this.excludeCurrent = this.root.dataset.excludeCurrent === "true";

      this.fixedCategory = (this.root.dataset.category || "").trim();
      this.fixedHashtag = (this.root.dataset.hashtag || "").trim();
      this.fixedField = this.fixedCategory ? "category" : "hashtag";
      this.fixedValue = this.fixedCategory || this.fixedHashtag;
      this.orTags = readOrTagsFromDom(this.root.dataset.orTagsSource);

      this.queryParamName = this.root.dataset.queryParamName || "hashtag";
      this.queryParamSource = this.root.dataset.queryParamSource || "none";
      this.queryParamKey = this.root.dataset.queryParamKey || "hashtag";

      // URLパラメータから複数タグをサポート (カンマ区切り)
      this.urlHashtags = [];
      if (this.queryParamSource === "url") {
        const sp = new URLSearchParams(location.search);
        const val = sp.get(this.queryParamKey);
        if (val) {
          this.urlHashtags = splitTags(val);
        }
      }
    }

    resolveTemplate() {
      const templateId = this.root.dataset.templateId;
      if (templateId) {
        const byId = document.getElementById(templateId);
        if (byId) return byId;
      }
      return this.root.querySelector("template");
    }

    resolveMoreButton() {
      if (this.root.dataset.moreButtonSelector) {
        return document.querySelector(this.root.dataset.moreButtonSelector);
      }
      if (this.root.id) {
        return document.querySelector(`[data-more-for="${this.root.id}"]`);
      }
      return this.root.parentElement
        ? this.root.parentElement.querySelector(".js-more-button")
        : null;
    }

    buildRequest(page, length, fieldName, fieldValue) {
      const params = new URLSearchParams();
      params.set("a", this.fields);
      params.set("l", String(length));
      params.set("p", String(page));

      if (fieldName && fieldValue) params.set(fieldName, fieldValue);

      if (this.queryParamSource === "url") {
        const sp = new URLSearchParams(location.search);
        const val = sp.get(this.queryParamKey);
        if (val) params.set(this.queryParamName, val);
      }

      return `${this.endpoint}?${params.toString()}`;
    }

    async fetchPage(page, length, fieldName, fieldValue) {
      if (!this.endpoint) {
        throw new Error("Search API endpoint is not configured");
      }

      logStagingZetaApiUsage(this.endpoint);
      const url = this.buildRequest(page, length, fieldName, fieldValue);
      const res = await fetch(url, { credentials: "omit" });
      if (!res.ok) {
        throw new Error(`API request failed: ${res.status}`);
      }
      const json = await res.json();
      return normalizeResponse(json);
    }

    getItemTags(item) {
      if (Array.isArray(item.hashtag)) {
        return item.hashtag
          .map((t) => String(t).replace(/^#/, "").trim())
          .filter(Boolean);
      }
      if (typeof item.hashtag === "string") {
        return splitTags(item.hashtag);
      }
      if (typeof item.tags === "string") {
        return splitTags(item.tags);
      }
      if (Array.isArray(item.tags)) {
        return item.tags
          .map((t) => String(t).replace(/^#/, "").trim())
          .filter(Boolean);
      }
      return [];
    }

    shouldKeep(item) {
      const url = normalizeUrl(item.url || item.link || "");
      if (!url) return false;

      if (this.excludeCurrent && url === this.currentPath) return false;

      if (this.mode === "related" && this.orTags.length > 0) {
        const itemTags = this.getItemTags(item);
        if (!itemTags.some((t) => this.orTags.includes(t))) return false;
      }

      if (this.root.dataset.categoryPathContains) {
        const needle = this.root.dataset.categoryPathContains;
        if (!url.includes(needle)) return false;
      }

      return true;
    }

    uniquePush(items) {
      items.forEach((item) => {
        const url = normalizeUrl(item.url || item.link || "");
        if (!url || this.seenUrls.has(url)) return;
        this.seenUrls.add(url);

        if (!this.shouldKeep(item)) return;

        item._normalizedUrl = url;
        item.dateFormatted = formatDate(item.date);
        item.isNew = isNewWithinDays(item.date);

        this.matchPool.push(item);
      });
    }

    bindItem(fragment, item) {
      fragment.querySelectorAll("[data-bind-text]").forEach((el) => {
        const key = el.getAttribute("data-bind-text");
        el.textContent = item[key] != null ? String(item[key]) : "";
      });

      fragment.querySelectorAll("[data-bind-href]").forEach((el) => {
        const key = el.getAttribute("data-bind-href");
        const val = item[key] != null ? String(item[key]) : "#";
        el.setAttribute("href", val);
      });

      fragment.querySelectorAll("[data-bind-src]").forEach((el) => {
        const key = el.getAttribute("data-bind-src");
        const val = item[key] != null ? String(item[key]) : "";
        el.setAttribute("src", val);
      });

      fragment.querySelectorAll("[data-bind-datetime]").forEach((el) => {
        const key = el.getAttribute("data-bind-datetime");
        const raw = item[key];
        if (raw == null) return;
        const dt = parseDate(raw);
        if (dt) {
          const y = dt.getFullYear();
          const m = String(dt.getMonth() + 1).padStart(2, "0");
          const d = String(dt.getDate()).padStart(2, "0");
          el.setAttribute("datetime", `${y}-${m}-${d}`);
        }
      });

      fragment.querySelectorAll("[data-bind-new-badge]").forEach((el) => {
        const key = el.getAttribute("data-bind-new-badge");
        const show = Boolean(item[key]);
        el.hidden = !show;
      });

      // カテゴリクラスを追加 (id: aa_yyyymmdd_bb の aa 部分)
      if (item.id) {
        const categoryCode = String(item.id).split("_")[0];
        fragment.querySelectorAll(".l-search-list-item-link").forEach((el) => {
          el.classList.add(`--category${categoryCode}`);
        });
      }
    }

    appendItems(items) {
      if (!this.template) return;

      const insertBeforeNode =
        this.template.parentElement === this.root ? this.template : null;

      const frag = document.createDocumentFragment();
      items.forEach((item) => {
        const node = this.template.content
          ? this.template.content.cloneNode(true)
          : document.importNode(this.template, true);

        this.bindItem(node, item);
        frag.appendChild(node);
      });

      if (insertBeforeNode) {
        this.root.insertBefore(frag, insertBeforeNode);
      } else {
        this.root.appendChild(frag);
      }

      this.renderedCount += items.length;
    }

    async collectUntil(needCount) {
      if (this.exhausted) return;

      const requestField =
        this.mode === "related"
          ? "hashtag"
          : this.queryParamSource === "url"
          ? this.queryParamName
          : this.fixedField;
      const requestValues =
        this.mode === "related" && this.orTags.length > 0
          ? this.orTags
          : this.urlHashtags.length > 0
          ? this.urlHashtags
          : [this.fixedValue || ""];

      const requestList = requestValues.length ? requestValues : [""];

      let safetyPageCount = 0;

      while (this.matchPool.length < needCount && !this.exhausted) {
        if (safetyPageCount >= this.maxFetchPages) {
          this.exhausted = true;
          break;
        }
        safetyPageCount += 1;

        const nextPage = this.currentPage + 1;
        this.currentPage = nextPage;

        let fetchedAny = false;

        for (let i = 0; i < requestList.length; i += 1) {
          const requestValue = requestList[i];
          const { items, total } = await this.fetchPage(nextPage, this.fetchSize, requestField, requestValue);

          this.total = Math.min(this.total, total);

          if (requestList.length === 1) {
            this.updateTotalCount(total);
          }

          fetchedAny = fetchedAny || items.length > 0;

          this.uniquePush(items);
        }

        if (!fetchedAny) {
          this.exhausted = true;
          break;
        }
      }
    }

    updateMoreButton() {
      if (!this.button) return;

      const noMoreByConfig = this.moreCount <= 0;
      const noMoreByData =
        (this.exhausted && this.matchPool.length === 0) ||
        (Number.isFinite(this.total) && this.renderedCount >= this.total && this.mode !== "related");

      this.button.hidden = noMoreByConfig || noMoreByData;
    }

    updateTotalCount(total) {
      if (!this.root.id) return;
      if (!Number.isInteger(total) || total < 0) return;

      const selector = `[data-total-for="${CSS.escape(this.root.id)}"]`;

      document.querySelectorAll(selector).forEach((element) => {
        element.textContent = `${total}件`;
      });
    }

    async renderBatch(batchSize) {
      if (this.loading) return;
      if (!this.template) return;

      this.loading = true;
      try {
        await this.collectUntil(batchSize);

        const toRender = this.matchPool.splice(0, batchSize);
        if (toRender.length > 0) {
          this.appendItems(toRender);
        }

        this.updateMoreButton();
      } catch (err) {
        console.error("[article-list] render error:", err);
        this.updateMoreButton();
      } finally {
        this.loading = false;
      }
    }

    async init() {
      if (!this.template) return;
      if (!this.endpoint) {
        console.error("[article-list] Search API endpoint is not configured");
        if (this.button) this.button.hidden = true;
        return;
      }

      await this.renderBatch(this.initialCount);
      this.updateMoreButton();

      if (this.button && this.moreCount > 0) {
        this.button.addEventListener("click", () => {
          this.renderBatch(this.moreCount);
        });
      }
    }
  }

  async function boot() {
    const runtimeConfig = await loadRuntimeApiConfig();

    document.querySelectorAll(SELECTOR).forEach((root) => {
      const instance = new ArticleList(root, runtimeConfig);
      instance.init();
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
